import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _possibleConstructorReturn from '@babel/runtime/helpers/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/getPrototypeOf';
import _assertThisInitialized from '@babel/runtime/helpers/assertThisInitialized';
import _createClass from '@babel/runtime/helpers/createClass';
import _inherits from '@babel/runtime/helpers/inherits';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { jsx } from '@emotion/core';
import { EMPTY_PARAGRAPH_NODE, bool, selectionEmpty, absoluteCoordinates, isEmptyParagraphNode, getDocument, uniqueId, isFunction, EDITOR_CLASS_NAME, toHTML, fromHTML, isArray, shouldUseDOMEnvironment, isPlainObject, ExtensionManager } from '@remirror/core';
import { PortalContainer, RemirrorPortals } from '@remirror/react-portals';
import { createEditorView, RemirrorSSR } from '@remirror/react-ssr';
import { asDefaultProps, propIsFunction, addKeyToElement, getElementProps, isRemirrorContextProvider, isRemirrorProvider, isManagedRemirrorProvider, cloneElement, isReactDOMElement, RemirrorType, isReactFragment, isRemirrorExtension, oneChildOnly } from '@remirror/react-utils';
export * from '@remirror/react-utils';
import { RemirrorThemeContext } from '@remirror/ui';
import { Fragment, PureComponent, createContext, Children, Component, useContext } from 'react';
import { baseExtensions } from '@remirror/core-extensions';
import _extends from '@babel/runtime/helpers/extends';
import { renderToString } from 'react-dom/server';

var defaultProps = asDefaultProps()({
  initialContent: EMPTY_PARAGRAPH_NODE,
  fallbackContent: EMPTY_PARAGRAPH_NODE,
  editable: true,
  usesBuiltInExtensions: true,
  attributes: {},
  usesDefaultStyles: true,
  label: '',
  styles: {},
  editorStyles: {},
  insertPosition: 'end',
  onDispatchTransaction: function onDispatchTransaction(tr) {
    return tr;
  },
  stringHandler: function stringHandler() {
    throw new Error('No valid string handler. In order to pass in `string` as `initialContent` to the remirror editor you must provide a valid stringHandler prop');
  }
});

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var defaultPositioner = {
  initialPosition: {
    top: -99999,
    left: -99999,
    right: 99999,
    bottom: 99999
  },
  hasChanged: function hasChanged(_ref) {
    var oldState = _ref.oldState,
        newState = _ref.newState;
    return !(bool(oldState) && oldState.doc.eq(newState.doc) && oldState.selection.eq(newState.selection));
  },
  isActive: function isActive(_ref2) {
    var newState = _ref2.newState;
    return !selectionEmpty(newState);
  },
  getPosition: function getPosition(_ref3) {
    var element = _ref3.element,
        view = _ref3.view,
        newState = _ref3.newState;
    return absoluteCoordinates({
      view: view,
      element: element,
      position: view.coordsAtPos(newState.selection.$anchor.pos)
    });
  }
};
/**
 * Render a menu which floats to the right at the beginning of an empty paragraph
 */

var floatingPositioner = _objectSpread({}, defaultPositioner, {
  isActive: function isActive(_ref4) {
    var newState = _ref4.newState;
    return selectionEmpty(newState) && isEmptyParagraphNode(newState.selection.$anchor.parent);
  },
  getPosition: function getPosition(_ref5) {
    var view = _ref5.view,
        element = _ref5.element,
        newState = _ref5.newState;

    if (!element.offsetParent) {
      return floatingPositioner.initialPosition;
    }

    var editorRect = element.offsetParent.getBoundingClientRect();
    var cursorRect = view.coordsAtPos(newState.selection.$anchor.pos);
    var top = cursorRect.top - editorRect.top;
    return _objectSpread({}, floatingPositioner.initialPosition, {
      top: top
    });
  }
});
/**
 * Render a centered bubble menu which becomes active whenever a selection is
 * made.
 *
 * @remarks
 *
 * The menu will horizontally center itself `from` / `to` bounds of the current
 * selection.
 *
 * - `right` should be used to absolutely position away from the right hand edge
 *   of the screen
 * - `left` should be used to determine absolute horizontal positioning.
 * - `bottom` absolutely positions the element above the text selection.
 * - `top` absolutely positions the element below the text selection
 */

var bubblePositioner = _objectSpread({}, defaultPositioner, {
  getPosition: function getPosition(_ref6) {
    var view = _ref6.view,
        element = _ref6.element,
        newState = _ref6.newState;
    var _newState$selection = newState.selection,
        from = _newState$selection.from,
        to = _newState$selection.to;
    var parent = element.offsetParent;

    if (!parent) {
      return bubblePositioner.initialPosition;
    } // These are in screen coordinates


    var start = view.coordsAtPos(from);
    var end = view.coordsAtPos(to); // The box in which the bubble menu is positioned, to use as an anchor

    var parentBox = parent.getBoundingClientRect(); // The bubble menu element

    var elementBox = element.getBoundingClientRect();
    var calculatedLeft = (start.left + end.left) / 2 - parentBox.left;
    var left = Math.min(parentBox.width - elementBox.width / 2, Math.max(calculatedLeft, elementBox.width / 2));
    return {
      right: left,
      left: left,
      bottom: Math.trunc(parentBox.bottom - start.top),
      top: Math.trunc(start.bottom - parentBox.top)
    };
  }
});
/**
 * Render a menu that is inline with the first character of the selection.
 *
 * @remarks
 *
 * The menu will center itself within the selection.
 *
 * - `right` should be used to absolutely position away from the right
 * hand edge of the screen.
 * - `left` should be used to absolutely position away from the left
 * hand edge of the screen.
 * - `bottom` absolutely positions the element above the text selection.
 * - `top` absolutely positions the element below the text selection
 */

var popupMenuPositioner = _objectSpread({}, defaultPositioner, {
  /**
   * Only active when the selection is empty (one character)
   */
  isActive: function isActive(_ref7) {
    var newState = _ref7.newState;
    return selectionEmpty(newState);
  },
  getPosition: function getPosition(_ref8) {
    var view = _ref8.view,
        element = _ref8.element,
        newState = _ref8.newState;
    var parent = element.offsetParent;

    if (!parent) {
      return bubblePositioner.initialPosition;
    } // Position at the start of the selection


    var start = view.coordsAtPos(newState.selection.from); // The box in which the bubble menu is positioned, to use as an anchor

    var parentBox = parent.getBoundingClientRect(); // The popup menu element

    var elementBox = element.getBoundingClientRect();
    var calculatedLeft = start.left - parentBox.left;
    var calculatedRight = parentBox.right - start.right;
    var left = calculatedLeft + elementBox.width > parentBox.width ? calculatedLeft - elementBox.width : calculatedLeft;
    var right = calculatedRight + elementBox.width > parentBox.width ? calculatedRight - elementBox.width : calculatedRight;
    return {
      right: right,
      left: left,
      bottom: Math.trunc(parentBox.bottom - start.top),
      top: Math.trunc(start.bottom - parentBox.top)
    };
  }
});

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var Remirror =
/*#__PURE__*/
function (_PureComponent) {
  _inherits(Remirror, _PureComponent);

  _createClass(Remirror, [{
    key: "doc",

    /**
     * The document to use when rendering.
     */
    get: function get() {
      return getDocument(this.props.forceEnvironment);
    }
    /**
     * A utility for quickly retrieving the extension manager.
     */

  }, {
    key: "manager",
    get: function get() {
      return this.props.manager;
    }
    /**
     * The Remirror Theme context which is made available to all components.
     */

  }], [{
    key: "getDerivedStateFromProps",

    /**
     * Allow the component to pull in context from the the `RemirrorThemeContext`
     */

    /**
     * Sets a flag to be a static remirror
     */

    /**
     * This is needed to manage the controlled component `value` prop and copy it
     * to the components state for internal usage.
     */
    value: function getDerivedStateFromProps(props, state) {
      var onStateChange = props.onStateChange,
          value = props.value;

      var newState = state.editor.newState,
          rest = _objectWithoutProperties(state, ["editor"]);

      if (onStateChange && value && value !== newState) {
        return _objectSpread$1({
          editor: {
            newState: value,
            oldState: newState
          }
        }, rest);
      }

      return null;
    }
    /**
     * Stores the Prosemirror EditorView dom element.
     */

  }]);

  function Remirror(_props, context) {
    var _this;

    _classCallCheck(this, Remirror);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Remirror).call(this, _props, context)); // Ensure that children is a render prop.

    _defineProperty(_assertThisInitialized(_this), "positionerMap", new Map());

    _defineProperty(_assertThisInitialized(_this), "uid", uniqueId({
      size: 10
    }));

    _defineProperty(_assertThisInitialized(_this), "portalContainer", new PortalContainer());

    _defineProperty(_assertThisInitialized(_this), "getState", function () {
      return _this.props.onStateChange && _this.props.value ? _this.props.value : _this.view.state;
    });

    _defineProperty(_assertThisInitialized(_this), "getTheme", function () {
      return _this.context;
    });

    _defineProperty(_assertThisInitialized(_this), "rootPropsConfig", {
      called: false
    });

    _defineProperty(_assertThisInitialized(_this), "getRootProps", function (options) {
      return _this.internalGetRootProps(options, null);
    });

    _defineProperty(_assertThisInitialized(_this), "internalGetRootProps", function (options, children) {
      var _objectSpread2;

      // Ensure that this is the first time `getRootProps` is being called during
      // this render.
      if (_this.rootPropsConfig.called) {
        throw new Error('`getRootProps` has been called MULTIPLE times. It should only be called ONCE during render.');
      }

      _this.rootPropsConfig.called = true;

      var _ref = options || Object.create(null),
          _ref$refKey = _ref.refKey,
          refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
          config = _objectWithoutProperties(_ref, ["refKey"]);

      var sx = _this.context.sx;
      var css = sx(_this.editorStyles);
      var extra = bool(css) ? {
        css: css
      } : {};
      return _objectSpread$1((_objectSpread2 = {}, _defineProperty(_objectSpread2, refKey, _this.onRef), _defineProperty(_objectSpread2, "key", _this.uid), _objectSpread2), extra, {}, config, {
        children: children || _this.renderChildren(null)
      });
    });

    _defineProperty(_assertThisInitialized(_this), "getPositionerProps", function (options) {
      var _defaultPositioner = _objectSpread$1({}, defaultPositioner, {}, options || Object.create(null)),
          _defaultPositioner$re = _defaultPositioner.refKey,
          refKey = _defaultPositioner$re === void 0 ? 'ref' : _defaultPositioner$re,
          config = _objectWithoutProperties(_defaultPositioner, ["refKey"]); // Create the onRef handler which will store the ref to the positioner
      // component


      var ref = _this.positionerRefFactory({
        positionerId: config.positionerId,
        position: config.initialPosition
      }); // Calculate the props


      var props = _this.calculatePositionProps(_objectSpread$1({}, config));

      var ret = _objectSpread$1({}, props, _defineProperty({}, refKey, ref));

      return ret;
    });

    _defineProperty(_assertThisInitialized(_this), "onRef", function (ref) {
      if (ref) {
        _this.editorRef = ref;

        _this.onRefLoad();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "positionerRefFactory", function (_ref2) {
      var positionerId = _ref2.positionerId,
          position = _ref2.position;
      return function (element) {
        if (!element) {
          return;
        } // Retrieve the current


        var current = _this.positionerMap.get(positionerId);

        if (!current || current.element !== element) {
          _this.positionerMap.set(positionerId, {
            element: element,
            prev: _objectSpread$1({}, position, {
              isActive: false
            })
          });
        }
      };
    });

    _defineProperty(_assertThisInitialized(_this), "getAttributes", function () {
      var ssr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      var attributes = _this.props.attributes;
      var propAttributes = isFunction(attributes) ? attributes(_this.eventListenerParams()) : attributes;
      var managerAttrs = _this.manager.attributes;

      var defaultAttributes = _objectSpread$1({
        role: 'textbox',
        'aria-multiline': 'true'
      }, !_this.props.editable ? {
        'aria-readonly': 'true'
      } : {}, {
        'aria-label': _this.props.label || ''
      }, managerAttrs, {
        class: [ssr && 'Prosemirror', EDITOR_CLASS_NAME, managerAttrs.class].filter(bool).join(' ')
      });

      return _objectSpread$1({}, defaultAttributes, {}, propAttributes);
    });

    _defineProperty(_assertThisInitialized(_this), "dispatchTransaction", function (tr) {
      tr = _this.props.onDispatchTransaction(tr, _this.getState());

      var state = _this.getState().apply(tr);

      _this.updateState({
        tr: tr,
        state: state,
        onUpdate: function onUpdate() {
          _this.manager.onTransaction({
            tr: tr,
            state: state
          });
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "controlledUpdate", function (state) {
      var updateHandler = _this.createUpdateStateHandler({
        state: state
      });

      _this.view.updateState(state);

      updateHandler();
    });

    _defineProperty(_assertThisInitialized(_this), "createUpdateStateHandler", function (_ref3) {
      var state = _ref3.state,
          triggerOnChange = _ref3.triggerOnChange,
          onUpdate = _ref3.onUpdate,
          tr = _ref3.tr;
      return function () {
        var updatedState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : state;
        var onChange = _this.props.onChange; // No need to continue if triggerOnChange is `false`

        if (!triggerOnChange) {
          return;
        }

        if (onUpdate) {
          onUpdate();
        }

        if (onChange) {
          onChange(_this.eventListenerParams({
            state: updatedState,
            tr: tr
          }));
        }
      };
    });

    _defineProperty(_assertThisInitialized(_this), "onBlur", function (event) {
      if (_this.props.onBlur) {
        _this.props.onBlur(_this.eventListenerParams(), event);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "onFocus", function (event) {
      if (_this.props.onFocus) {
        _this.props.onFocus(_this.eventListenerParams(), event);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "setContent", function (content) {
      var triggerOnChange = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var state = _this.createStateFromContent(content);

      _this.updateState({
        state: state,
        triggerOnChange: triggerOnChange
      });
    });

    _defineProperty(_assertThisInitialized(_this), "clearContent", function () {
      var triggerOnChange = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      _this.setContent(_this.props.fallbackContent, triggerOnChange);
    });

    _defineProperty(_assertThisInitialized(_this), "getText", function (state) {
      return function () {
        var lineBreakDivider = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '\n\n';

        var _ref4 = state || _this.state.editor.newState,
            doc = _ref4.doc;

        return doc.textBetween(0, doc.content.size, lineBreakDivider);
      };
    });

    _defineProperty(_assertThisInitialized(_this), "getHTML", function (state) {
      return function () {
        return toHTML({
          node: (state || _this.state.editor.newState).doc,
          schema: _this.manager.data.schema,
          doc: _this.doc
        });
      };
    });

    _defineProperty(_assertThisInitialized(_this), "getJSON", function (state) {
      return function () {
        return (state || _this.state.editor.newState).toJSON();
      };
    });

    _defineProperty(_assertThisInitialized(_this), "getObjectNode", function (state) {
      return function () {
        return (state || _this.state.editor.newState).doc.toJSON();
      };
    });

    _defineProperty(_assertThisInitialized(_this), "createStateFromContent", function (content) {
      var _this$props = _this.props,
          stringHandler = _this$props.stringHandler,
          fallback = _this$props.fallbackContent;
      return _this.manager.createState({
        content: content,
        doc: _this.doc,
        stringHandler: stringHandler,
        fallback: fallback
      });
    });

    propIsFunction(_props.children); // Initialize the manager and create the initial state.

    _this.manager.init({
      getState: _this.getState,
      getTheme: _this.getTheme,
      portalContainer: _this.portalContainer
    });

    _this.state = _this.createInitialState(); // Create the ProsemirrorView and initialize our extension manager with it.

    _this.view = _this.createView();

    _this.manager.initView(_this.view);

    return _this;
  }
  /**
   * Reinitialize the Editor's manager when a new one is passed in via props.
   *
   * TODO check whether or not the schema has changed and log a warning. Schema
   * shouldn't change.
   */


  _createClass(Remirror, [{
    key: "updateExtensionManager",
    value: function updateExtensionManager() {
      this.manager.init({
        getState: this.getState,
        getTheme: this.getTheme,
        portalContainer: this.portalContainer
      }).initView(this.view);
    }
    /**
     * Retrieve the editor state. This is passed through to the extension manager.
     */

  }, {
    key: "createInitialState",

    /**
     * Create the initial React state which stores copies of the Prosemirror
     * editor state. Our React state also keeps track of the previous active
     * state.
     *
     * At this point both oldState and newState point to the same state object.
     */
    value: function createInitialState() {
      var suppressHydrationWarning = this.props.suppressHydrationWarning;
      var newState = this.createStateFromContent(this.props.initialContent);
      return {
        editor: {
          newState: newState,
          oldState: newState
        },
        shouldRenderClient: suppressHydrationWarning ? false : undefined
      };
    }
    /**
     * Create the prosemirror editor view.
     */

  }, {
    key: "createView",
    value: function createView() {
      var _this2 = this;

      return createEditorView(undefined, {
        state: this.state.editor.newState,
        nodeViews: this.manager.data.nodeViews,
        dispatchTransaction: this.dispatchTransaction,
        attributes: function attributes() {
          return _this2.getAttributes();
        },
        editable: function editable() {
          return _this2.props.editable;
        }
      }, this.props.forceEnvironment);
    }
  }, {
    key: "calculatePositionProps",

    /**
     * Returns the positioner props for a given positionerId.
     */
    value: function calculatePositionProps(_ref5) {
      var initialPosition = _ref5.initialPosition,
          getPosition = _ref5.getPosition,
          hasChanged = _ref5.hasChanged,
          isActive = _ref5.isActive,
          positionerId = _ref5.positionerId;
      var positionerMapItem = this.positionerMap.get(positionerId);

      var positionerProps = _objectSpread$1({
        isActive: false
      }, initialPosition); // No element exist yet - so we can return early


      if (!positionerMapItem) {
        return positionerProps;
      } // Nothing has changed so return the prev value.


      if (!hasChanged(this.state.editor)) {
        return positionerMapItem.prev;
      }

      var element = positionerMapItem.element,
          prev = positionerMapItem.prev;

      var params = _objectSpread$1({
        element: element,
        view: this.view
      }, this.state.editor);

      positionerProps.isActive = isActive(params);

      if (!positionerProps.isActive) {
        if (prev.isActive) {
          // This has changed so store the new value.
          this.positionerMap.set(positionerId, {
            element: element,
            prev: positionerProps
          });
          return positionerProps;
        }

        return prev;
      }

      positionerProps = _objectSpread$1({}, positionerProps, {}, getPosition(params));
      this.positionerMap.set(positionerId, {
        element: element,
        prev: positionerProps
      });
      return positionerProps;
    }
    /**
     * This sets the attributes that wrap the outer prosemirror node.
     */

  }, {
    key: "updateState",

    /**
     * Updates the state either by calling onStateChange when it exists or
     * directly setting the internal state via a `setState` call.
     */
    value: function updateState(_ref6) {
      var state = _ref6.state,
          _ref6$triggerOnChange = _ref6.triggerOnChange,
          triggerOnChange = _ref6$triggerOnChange === void 0 ? true : _ref6$triggerOnChange,
          onUpdate = _ref6.onUpdate,
          tr = _ref6.tr;
      var onStateChange = this.props.onStateChange;
      var updateHandler = this.createUpdateStateHandler({
        state: state,
        triggerOnChange: triggerOnChange,
        onUpdate: onUpdate
      }); // Check if this is a controlled component.

      if (onStateChange) {
        onStateChange(this.editorStateEventListenerParams({
          oldState: this.state.editor.newState,
          newState: state,
          tr: tr
        }));
      } else {
        // Update the internal prosemirror state. This happens before we update
        // the component's copy of the state.
        this.view.updateState(state); // This is not a controlled component so we need to manage firing of
        // setState

        this.setState(function (_ref7) {
          var newState = _ref7.editor.newState;
          return {
            editor: {
              oldState: newState,
              newState: state
            }
          }; // Move update handler out from callback and directly after
          // this.setState To prevent updates from only happening with stale data.
        }, updateHandler);
      }
    }
    /**
     * Adds the prosemirror view to the dom in the position specified via the
     * component props.
     */

  }, {
    key: "addProsemirrorViewToDom",
    value: function addProsemirrorViewToDom(reactRef, viewDom) {
      if (this.props.insertPosition === 'start') {
        reactRef.insertBefore(viewDom, reactRef.firstChild);
      } else {
        reactRef.appendChild(viewDom);
      }
    }
    /**
     * Called once the container dom node (`this.editorRef`) has been initialized
     * after the component mounts.
     *
     * This method handles the cases where the dom is not focused.
     */

  }, {
    key: "onRefLoad",
    value: function onRefLoad() {
      if (!this.editorRef) {
        throw Error('Something went wrong when initializing the text editor. Please check your setup.');
      }

      var _this$props2 = this.props,
          autoFocus = _this$props2.autoFocus,
          onFirstRender = _this$props2.onFirstRender,
          onStateChange = _this$props2.onStateChange;
      this.addProsemirrorViewToDom(this.editorRef, this.view.dom);

      if (autoFocus) {
        this.view.focus();
      }

      if (onFirstRender) {
        onFirstRender(this.eventListenerParams());
      } // Handle setting the state when this is a controlled component


      if (onStateChange) {
        onStateChange(this.editorStateEventListenerParams());
      }

      this.view.dom.addEventListener('blur', this.onBlur);
      this.view.dom.addEventListener('focus', this.onFocus);
    }
    /**
     * This is purely used to indicate to the component that this is a client
     * environment when using the `suppressHydrationWarning` prop.
     */

  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      var suppressHydrationWarning = this.props.suppressHydrationWarning;

      if (suppressHydrationWarning) {
        this.setState({
          shouldRenderClient: true
        });
      }
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(_ref8, prevState) {
      var _this3 = this;

      var editable = _ref8.editable,
          prevManager = _ref8.manager;
      // Ensure that children is still a render prop
      propIsFunction(this.props.children); // Check whether the editable prop has been updated

      if (this.props.editable !== editable && this.view && this.editorRef) {
        this.view.setProps(_objectSpread$1({}, this.view.props, {
          editable: function editable() {
            return _this3.props.editable;
          }
        }));
      } // Check if the manager has changed


      if (!prevManager.isEqual(this.props.manager)) {
        this.updateExtensionManager();
        this.view.setProps(_objectSpread$1({}, this.view.props, {
          nodeViews: this.manager.data.nodeViews
        })); // The following converts the current content to HTML and then uses the
        // new manager schema to convert it back into a ProsemirrorNode for
        // compatibility with the new manager.

        var htmlString = toHTML({
          node: this.state.editor.newState.doc,
          schema: prevManager.schema
        });
        var newContent = fromHTML({
          schema: this.manager.schema,
          content: htmlString,
          doc: this.doc
        });
        this.setContent(newContent, true);
      }

      var newState = this.state.editor.newState; // Check if this is controlled component and run the post update handler

      if (this.props.onStateChange && newState !== prevState.editor.newState) {
        // The update was caused by an internal change
        this.controlledUpdate(newState);
      }
    }
    /**
     * Called when the component unmounts and is responsible for cleanup.
     *
     * @remarks
     *
     * - Removes listeners for the editor blur and focus events
     * - Destroys the state for each plugin
     * - Destroys the prosemirror view
     */

  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.view.dom.removeEventListener('blur', this.onBlur);
      this.view.dom.removeEventListener('focus', this.onFocus);
      var editorState = this.state.editor.newState;
      this.view.state.plugins.forEach(function (plugin) {
        var state = plugin.getState(editorState);

        if (state && state.destroy) {
          state.destroy();
        }
      });
      this.view.destroy();
    }
    /**
     * Listener for editor 'blur' events
     */

  }, {
    key: "baseListenerParams",

    /**
     * The params used in the event listeners and the state listener
     */
    value: function baseListenerParams(_ref9) {
      var state = _ref9.state,
          tr = _ref9.tr;
      return {
        tr: tr,
        internalUpdate: !tr,
        view: this.view,
        getHTML: this.getHTML(state),
        getJSON: this.getJSON(state),
        getObjectNode: this.getObjectNode(state),
        getText: this.getText(state)
      };
    }
    /**
     * Creates the parameters passed into all event listener handlers.
     * e.g. `onChange`
     */

  }, {
    key: "eventListenerParams",
    value: function eventListenerParams() {
      var _ref10 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Object.create(null),
          state = _ref10.state,
          tr = _ref10.tr;

      return _objectSpread$1({}, this.baseListenerParams({
        tr: tr
      }), {
        state: state || this.state.editor.newState
      });
    }
    /**
     * The params passed into onStateChange (within controlled components)
     */

  }, {
    key: "editorStateEventListenerParams",
    value: function editorStateEventListenerParams() {
      var _ref11 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Object.create(null),
          newState = _ref11.newState,
          oldState = _ref11.oldState,
          tr = _ref11.tr;

      return _objectSpread$1({}, this.baseListenerParams({
        state: newState,
        tr: tr
      }), {
        newState: newState || this.state.editor.newState,
        oldState: oldState || this.state.editor.oldState,
        createStateFromContent: this.createStateFromContent
      });
    }
  }, {
    key: "renderChildren",

    /**
     * Checks whether this is an SSR environment and returns a child array with
     * the SSR component
     *
     * @param children
     */
    value: function renderChildren(child) {
      var _this$props3 = this.props,
          forceEnvironment = _this$props3.forceEnvironment,
          insertPosition = _this$props3.insertPosition,
          suppressHydrationWarning = _this$props3.suppressHydrationWarning;
      var shouldRenderClient = this.state.shouldRenderClient;
      var children = isArray(child) ? child : [child];

      if (shouldUseDOMEnvironment(forceEnvironment) && (!suppressHydrationWarning || shouldRenderClient)) {
        return children;
      }

      var ssrElement = this.renderSSR();
      return (insertPosition === 'start' ? [ssrElement].concat(_toConsumableArray(children)) : [].concat(_toConsumableArray(children), [ssrElement])).map(addKeyToElement);
    }
    /**
     * Return a JSX Element to be used within the domless environment.
     */

  }, {
    key: "renderSSR",
    value: function renderSSR() {
      return jsx(RemirrorSSR, {
        attributes: this.getAttributes(true),
        state: this.state.editor.newState,
        manager: this.manager,
        editable: this.props.editable
      });
    }
  }, {
    key: "renderReactElement",
    value: function renderReactElement() {
      var element = this.props.children(_objectSpread$1({}, this.renderParams));

      var _getElementProps = getElementProps(element),
          children = _getElementProps.children,
          props = _objectWithoutProperties(_getElementProps, ["children"]);

      if (this.rootPropsConfig.called) {
        // Simply return the element as this method can never actually be called
        // within a domless environment
        return element;
      } else if ( // When called by a provider `getRootProps` can't actually be called until
      // the jsx is generated. Check if this is being rendered via any remirror
      // context provider. In this case `getRootProps` **must** be called by the
      // consumer.
      isRemirrorContextProvider(element) || isRemirrorProvider(element) || isManagedRemirrorProvider(element)) {
        var childAsRoot = element.props.childAsRoot;
        return childAsRoot ? cloneElement(element, props, this.renderClonedElement(children, childAsRoot)) : element;
      } else {
        return isReactDOMElement(element) ? this.renderClonedElement(element) : jsx("div", this.internalGetRootProps(undefined, this.renderChildren(element)));
      }
    }
    /**
     * Clones the passed element when `getRootProps` hasn't yet been called.
     *
     * This method also supports rendering the children within a domless environment where necessary.
     */

  }, {
    key: "renderClonedElement",
    value: function renderClonedElement(element, rootProps) {
      var _getElementProps2 = getElementProps(element),
          children = _getElementProps2.children,
          rest = _objectWithoutProperties(_getElementProps2, ["children"]);

      var props = isPlainObject(rootProps) ? _objectSpread$1({}, rootProps, {}, rest) : rest;
      return cloneElement(element, this.internalGetRootProps(props, this.renderChildren(children)));
    }
  }, {
    key: "render",
    value: function render() {
      // Reset the status of roots props being called
      this.rootPropsConfig.called = false;
      return jsx(Fragment, null, this.renderReactElement(), jsx(RemirrorPortals, {
        portalContainer: this.portalContainer
      }));
    }
  }, {
    key: "editorStyles",

    /**
     * Provides access to the dynamically generated `css-in-js` editor styles.
     */
    get: function get() {
      var styles = [this.props.editorStyles, this.props.css, this.props.styles]; // Inject the styles from extensions

      styles.unshift(this.manager.data.styles);

      if (this.props.usesDefaultStyles) {
        styles.unshift({
          variant: 'styles.remirror:editor'
        });
      }

      return styles;
    }
    /**
     * The external `getRootProps` that is used to spread props onto a desired
     * holder element for the prosemirror view.
     */

  }, {
    key: "renderParams",
    get: function get() {
      return {
        /* Properties */
        uid: this.uid,
        manager: this.manager,
        view: this.view,
        state: this.state.editor,

        /* Mapped methods */
        actions: this.manager.data.actions,
        helpers: this.manager.data.helpers,

        /* Getter Methods */
        getRootProps: this.getRootProps,
        getPositionerProps: this.getPositionerProps,

        /* Setter Methods */
        clearContent: this.clearContent,
        setContent: this.setContent
      };
    }
  }]);

  return Remirror;
}(PureComponent);

_defineProperty(Remirror, "defaultProps", defaultProps);

_defineProperty(Remirror, "contextType", RemirrorThemeContext);

_defineProperty(Remirror, "$$remirrorType", RemirrorType.Editor);

/**
 * Creates a ReactContext for the Remirror editor
 */
var RemirrorContext = createContext(null);
/**
 * Creates a ReactContext for the RemirrorManager component
 */

var RemirrorManagerContext = createContext(null);

/**
 * This component consumes any directly nested RemirrorExtension components and creates a
 * manager instance which is passed into the created context for any children to consume.
 *
 * It allows for a more React-ish way of managing Prosemirror.
 */

var RemirrorManager =
/*#__PURE__*/
function (_Component) {
  _inherits(RemirrorManager, _Component);

  function RemirrorManager() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, RemirrorManager);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(RemirrorManager)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "cachedManager", ExtensionManager.create(_this.withBaseExtensions([])));

    return _this;
  }

  _createClass(RemirrorManager, [{
    key: "withBaseExtensions",

    /**
     * Prepends the base extensions to the configured extensions where applicable.
     */
    value: function withBaseExtensions(extensions) {
      var useBaseExtensions = this.props.useBaseExtensions;
      var base = (useBaseExtensions ? baseExtensions : []).filter(function (_ref) {
        var name = _ref.extension.name;
        return !extensions.some(function (_ref2) {
          var extension = _ref2.extension;
          return extension.name === name;
        });
      });
      return [].concat(_toConsumableArray(base), _toConsumableArray(extensions));
    }
  }, {
    key: "render",
    value: function render() {
      return jsx(RemirrorManagerContext.Provider, {
        value: this.manager
      }, this.props.children);
    }
  }, {
    key: "manager",

    /**
     * Calculate the manager based on the baseExtension and passed in props.
     */
    get: function get() {
      var extensions = [];
      var children = this.props.children;
      var resolvedChildren = Children.toArray(children).reduce(function (acc, child) {
        if (isReactFragment(child)) {
          return [].concat(_toConsumableArray(acc), _toConsumableArray(Children.toArray(child.props.children)));
        } else {
          return [].concat(_toConsumableArray(acc), [child]);
        }
      }, []);
      resolvedChildren.forEach(function (child) {
        if (!isRemirrorExtension(child)) {
          return;
        }

        var _child$props = child.props,
            Constructor = _child$props.Constructor,
            _ = _child$props.children,
            _child$props$priority = _child$props.priority,
            priority = _child$props$priority === void 0 ? 2 : _child$props$priority,
            options = _objectWithoutProperties(_child$props, ["Constructor", "children", "priority"]);

        extensions.push({
          extension: new Constructor(options),
          priority: priority
        });
      });
      var newManager = ExtensionManager.create(this.withBaseExtensions(extensions)); // Only update the manager when it has changed to prevent unnecessary re-rendering

      if (newManager.isEqual(this.cachedManager)) {
        return this.cachedManager;
      }

      this.cachedManager = newManager;
      return newManager;
    }
  }]);

  return RemirrorManager;
}(Component);

_defineProperty(RemirrorManager, "defaultProps", asDefaultProps()({
  useBaseExtensions: true
}));

/**
 * This component creates an extension that will be picked up by a parent RemirrorManager.
 *
 * @remarks
 *
 * This is a more React-ish way of configuring the editor extensions. The alternative to creating a
 * component tree structure would be manually dealing with the ExtensionManager.
 *
 * Without `RemirrorExtension`
 * ```tsx
 * const WithoutRemirrorExtensions = () => {
 *   const handler = () => {
 *     log('something happened');
 *   }
 *
 *   const manager = ExtensionManager.create([
 *     ...baseExtensions,
 *     { extension: new TestExtension({ handler }), priority: 1 },
 *     new PlaceholderExtension({ emptyNodeClass: 'empty' })
 *   ]);
 *
 *   return (
 *    <RemirrorProvider manager={manager}>
 *      <Component />
 *    </RemirrorProvider>
 *   )
 * }
 * ```
 *
 * With `RemirrorExtension`
 * ```tsx
 * const WithRemirrorExtensions = () => {
 *   const handler = () => {
 *     log('something happened');
 *   }
 *
 *   return (
 *    <RemirrorManager>
 *      <ManagedRemirrorProvider>
 *        <Component />
 *      </ManagedRemirrorProvider>
 *      <RemirrorExtension Constructor={TestExtension} handler={handler} priority={1} />
 *      <RemirrorExtension Constructor={PlaceholderExtension} emptyNodeClass='empty' />
 *    </RemirrorManager>
 *   )
 * }
 * ```
 *
 * Choose the pattern that best suits your style.
 */
var RemirrorExtension = function RemirrorExtension(_props) {
  return null;
};
RemirrorExtension.$$remirrorType = RemirrorType.Extension;

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * This provides access to the Remirror Editor context using hooks.
 *
 * ```ts
 * import { RemirrorProvider, useRemirrorContext } from 'remirror';
 *
 * function HooksComponent(props) {
 *   // This pull the remirror props out from the context.
 *   const { getPositionerProps } = useRemirrorContext();
 *
 *   return <Menu {...getPositionerProps()} />;
 * }
 *
 * class App extends Component {
 *   render() {
 *     return (
 *       <RemirrorProvider>
 *         <HooksComponent />
 *       </RemirrorProvider>
 *     );
 *   }
 * }
 * ```
 */
var useRemirrorContext = function useRemirrorContext() {
  var params = useContext(RemirrorContext);

  if (!params) {
    throw new Error('There is no remirror context defined.');
  }

  return params;
};
/**
 * A low level context picker to obtain the manager from within a RemirrorManager context
 */

var useRemirrorManager = function useRemirrorManager() {
  var manager = useContext(RemirrorManagerContext);

  if (!manager) {
    throw new Error('There is no manager defined within the context.');
  }

  return manager;
};
/**
 * Hooks to receive the positioner props and add them to your component
 *
 * ```ts
 * import { bubblePositioner } from '@remirror/react';
 *
 * const MenuComponent: FC = () => {
 *   const { isActive, bottom, left } = usePositioner({positionerId: 'bubbleMenu', positioner: bubblePositioner })
 *
 *   return (
 *     <div style={{ bottom, left }}>
 *       <MenuIcon {...properties} />
 *     </div>
 *   );
 * }
 *
 * <RemirrorProvider extensions={[]}>
 *   <MenuComponent />
 * </RemirrorProvider>
 * ```
 *
 * @param params
 * @param params.positioner
 * @param params.positionerId
 * @param params.refKey
 */

var usePositioner = function usePositioner(_ref) {
  var positioner = _ref.positioner,
      rest = _objectWithoutProperties(_ref, ["positioner"]);

  var _useRemirrorContext = useRemirrorContext(),
      getPositionerProps = _useRemirrorContext.getPositionerProps;

  return getPositionerProps(_objectSpread$2({}, positioner, {}, rest));
};

/**
 * This purely exists so that we know when the remirror editor has been called
 * with a provider as opposed to directly as a render prop by the user.
 *
 * It's important because when called directly by the user `getRootProps` is
 * automatically called when the render prop is called. However when called via
 * a Provider the render prop renders the context component and it's not until
 * the element is actually rendered that the getRootProp in any nested
 * components is called.
 */
var RemirrorContextProvider = function RemirrorContextProvider(_ref) {
  var _ = _ref.childAsRoot,
      props = _objectWithoutProperties(_ref, ["childAsRoot"]);

  var Component = RemirrorContext.Provider;
  return jsx(Component, props);
};

RemirrorContextProvider.$$remirrorType = RemirrorType.ContextProvider;
RemirrorContextProvider.defaultProps = {
  childAsRoot: false
};
/**
 * The RemirrorProvider which injects context into it's child component.
 *
 * @remarks
 * This only supports one child. At the moment if that that child is an built in
 * html string element then it is also treated as the
 *
 * These can either be consumed using React Hooks
 * - `useRemirrorContext`
 * - `usePositioner`
 */

var RemirrorProvider = function RemirrorProvider(_ref2) {
  var children = _ref2.children,
      childAsRoot = _ref2.childAsRoot,
      props = _objectWithoutProperties(_ref2, ["children", "childAsRoot"]);

  return jsx(Remirror, props, function (value) {
    return jsx(RemirrorContextProvider, {
      value: value,
      childAsRoot: childAsRoot
    }, oneChildOnly(children));
  });
};
RemirrorProvider.$$remirrorType = RemirrorType.EditorProvider;

/**
 * Renders the content while pulling the manager from the context and passing it
 * on to the RemirrorProvider.
 *
 * If no manager exists the child components are not rendered.
 */
var ManagedRemirrorProvider = function ManagedRemirrorProvider(_ref3) {
  var children = _ref3.children,
      props = _objectWithoutProperties(_ref3, ["children"]);

  var manager = useRemirrorManager();
  return jsx(RemirrorProvider, _extends({}, props, {
    manager: manager
  }), children);
};
ManagedRemirrorProvider.$$remirrorType = RemirrorType.ManagedEditorProvider;

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Retrieve the extension manager from your remirror component tree.
 *
 * @remarks
 *
 * When building your multi-platform editor you will want to use **one** schema across
 * all your environments. Since Prosemirror renders in the DOM it makes sense to set
 * up the schema in your DOM based react code.
 *
 * But how to then use that same schema across your platforms?
 *
 * This function provides a way of doing just that. It requires that you set up your
 * editor with with a wrapper component that uses the `RemirrorManager` and then provide
 * an _insertion point_ prop (`children` by default) to place a component within the
 * `RemirrorManager`.
 *
 * This function uses the `prop` you provide to insert a component whose sole purpose is
 * to pull the extension manager from the React context and pass it back to the consumer
 * of this function.
 *
 * ```ts
 * // client.ts
 * export const MyEditorWrapper = ({ children }) => {
 *   return (
 *     <RemirrorManager>
 *       {children}
 *       <RemirrorExtension Constructor={NewExtension} />
 *       <RemirrorExtension Constructor={PlaceholderExtension} emptyNodeClass='empty' />
 *     </RemirrorManager>
 *   );
 * };
 *
 * // server.ts
 * import { MyEditorWrapper } from './client';
 *
 * const manager = await getManagerFromComponentTree({
 *   Component: <MyEditorWrapper />,
 *   prop: 'children',
 * });
 *
 * // For example now you have access to the schema
 * const { schema } =  manager;
 * ```
 *
 * This is useful in DOM-less (e.g. server-side) environments.
 */
var getManagerFromComponentTree = function getManagerFromComponentTree(_ref) {
  var Component = _ref.Component,
      _ref$prop = _ref.prop,
      prop = _ref$prop === void 0 ? 'children' : _ref$prop,
      _ref$extraProps = _ref.extraProps,
      extraProps = _ref$extraProps === void 0 ? Object.create(null) : _ref$extraProps;
  return new Promise(function (resolve, reject) {
    var MangerRetriever = function MangerRetriever() {
      var manager = useRemirrorManager();
      resolve(manager);
      return null;
    };

    var props = _objectSpread$3({}, extraProps, _defineProperty({}, prop, jsx(MangerRetriever, null)));

    renderToString(jsx(Component, props));
    reject(new Error("The manager was not found. Please check that `".concat(Component.displayName || Component.name, "` has a prop called `").concat(prop, "` which is rendered within the `<RemirrorManager />` context")));
  });
};

export { ManagedRemirrorProvider, Remirror, RemirrorExtension, RemirrorManager, RemirrorProvider, bubblePositioner, defaultPositioner, floatingPositioner, getManagerFromComponentTree, popupMenuPositioner, usePositioner, useRemirrorContext, useRemirrorManager };
//# sourceMappingURL=react.esm.js.map
